import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Icon from 'widgets/Icon/Icon';
import Modal from 'widgets/Modal/Modal';
import Button from 'widgets/Button';

import { getProductImageUrl, IMAGE_SIZES } from '../../../utils/productImages';

function unifyTouchEvents(e) {
  return e.changedTouches ? e.changedTouches[0] : e;
}

const ZOOM_RATIO = 1.4;
const calculateMaxZoomRatio = () => {
  const maxNonZoomedWidth = Math.min(window.innerHeight, window.innerWidth);
  return IMAGE_SIZES.S_1280 / maxNonZoomedWidth;
};

const getImageSize = (image) => (image
  ? image.getBoundingClientRect().width
  : window.innerWidth);
const shouldShowZoomIn = (image) => (getImageSize(image) < IMAGE_SIZES.S_1280);
const shouldShowZoomOut = (zoomRatio, initialRatio) => (zoomRatio > initialRatio);

const restrictMaxDragHorizontal = (drag, zoom, image, zoomChange) => {
  const imgSizeParam = zoomChange || 1;
  const maxDragHorizontal = ((getImageSize(image) * imgSizeParam - window.innerWidth) / 2) / zoom;
  if (maxDragHorizontal < 0) { return 0; }
  return Math.abs(drag) > maxDragHorizontal ? maxDragHorizontal * Math.sign(drag) : drag;
};
const restrictMaxDragVertical = (drag, zoom, image, zoomChange) => {
  const imgSizeParam = zoomChange || 1;
  const maxDragVertical = ((getImageSize(image) * imgSizeParam - window.innerHeight) / 2) / zoom;
  if (maxDragVertical < 0) { return 0; }
  return Math.abs(drag) > maxDragVertical ? maxDragVertical * Math.sign(drag) : drag;
};
export default class ImageGalleryZoom extends PureComponent {
  static propTypes = {
    sku: PropTypes.string,
    images: PropTypes.array,
    currentImageIndex: PropTypes.number,
    hideModal: PropTypes.func,
  };

  state = {
    zoomRatio: 1,
    initialRatio: 1,
    dragSizeHorizontal: 0,
    dragSizeVertical: 0,
    currentImageIndex: this.props.currentImageIndex || 0,
    maxZoomRatio: 10,
  };

  componentDidMount() {
    this.wrapper.addEventListener('mousedown', this.lock);
    this.wrapper.addEventListener('touchstart', this.lock);

    this.wrapper.addEventListener('mousemove', this.drag, false);
    this.wrapper.addEventListener('touchmove', this.drag, false);

    this.wrapper.addEventListener('mouseup', this.release);
    this.wrapper.addEventListener('touchend', this.release);
    this.wrapper.addEventListener('touchend', this.release);

    document.querySelector('.main-app-view').classList.add('u-overflowHide');

    this.buttonWrapper.addEventListener('mousemove', this.release);
    document.addEventListener('gesturestart', this.muteEventStart);
    document.addEventListener('gesturechange', this.resizeIfNeededOnPinch);
    document.addEventListener('gestureend', this.muteEventStop);
    this.setState({ maxZoomRatio: calculateMaxZoomRatio() });
  }

  componentWillUnmount() {
    this.wrapper.removeEventListener('mousedown', this.lock);
    this.wrapper.removeEventListener('touchstart', this.lock);

    this.wrapper.removeEventListener('mousemove', this.drag, false);
    this.wrapper.removeEventListener('touchmove', this.drag, false);

    this.wrapper.removeEventListener('mouseup', this.release);
    this.wrapper.removeEventListener('touchend', this.release);
    document.querySelector('.main-app-view').classList.remove('u-overflowHide');
    this.buttonWrapper.removeEventListener('mousemove', this.release);
    document.removeEventListener('gesturestart', this.muteEventStart);
    document.removeEventListener('gesturechange', this.resizeIfNeededOnPinch);
    document.removeEventListener('gestureend', this.muteEventStop);
  }

  imageRef = React.createRef();

  isLastImageShown = () => {
    const numberOfImages = this.props.images.length;
    return this.state.currentImageIndex === numberOfImages - 1;
  };

  isFirstImageShown = () => this.state.currentImageIndex === 0;

  zoom = (zoomChange) => {
    const zoom = this.state.zoomRatio * Math.sqrt(zoomChange);
    this.setState(({ dragSizeHorizontal, dragSizeVertical, initialRatio, maxZoomRatio }) => {
      const adjustedHorizontalDrag = restrictMaxDragHorizontal(
        dragSizeHorizontal,
        zoom,
        this.imageRef.current,
        zoomChange
      );
      const adjustedVerticalDrag = restrictMaxDragVertical(
        dragSizeVertical,
        zoom,
        this.imageRef.current,
        zoomChange
      );
      const shouldZoomIn = getImageSize(this.imageRef.current) * zoomChange < IMAGE_SIZES.S_1280;
      return {
        zoomRatio: Math.max(Math.min(zoom, maxZoomRatio), initialRatio),
        dragSizeHorizontal: adjustedHorizontalDrag,
        dragSizeVertical: adjustedVerticalDrag,
        draggable: zoom > 1,
        shouldZoomIn,
      };
    });
  };

  muteEventStart = (e) => {
    this.setState({ noDrag: true });
    e.preventDefault();
  }

  muteEventStop = (e) => {
    this.setState({ noDrag: false });
    e.preventDefault();
  }

  resizeIfNeededOnPinch = (e) => {
    e.preventDefault();
    if (e.scale > 1) {
      this.zoom(1.1);
    }
    if (e.scale < 1) {
      this.zoom(1 / 1.1);
    }
  };

  adjustImageToWindow = () => {
    const imageSize = getImageSize(this.imageRef.current);
    if (this.state.initialRatio > 0 && this.state.initialRatio < this.state.maxZoomRatio) {
      this.setState(({ initialRatio }) => ({ zoomRatio: initialRatio }));
    } else {
      const initialRatio = Math.min(window.innerWidth / imageSize, window.innerHeight / imageSize);

      if (initialRatio > 0 && initialRatio < this.state.maxZoomRatio) {
        this.setState({
          zoomRatio: initialRatio,
          initialRatio,
        });
      }
    }
  };

  zoomIn = () => {
    this.zoom(ZOOM_RATIO);
  };

  zoomOut = () => {
    this.zoom(1 / ZOOM_RATIO);
  };

  nextImage = () => {
    this.setState(({ currentImageIndex, initialRatio }) =>
      ({ currentImageIndex: currentImageIndex + 1, zoomRatio: initialRatio }));
    this.adjustImageToWindow();
  };

  prevImage = () => {
    this.setState(({ currentImageIndex, initialRatio }) =>
      ({ currentImageIndex: currentImageIndex - 1, zoomRatio: initialRatio }));
    this.adjustImageToWindow();
  };

  lock = (e) => {
    if (e.type !== 'touchstart') { e.preventDefault(); }
    this.cursorPosition = { x: unifyTouchEvents(e).clientX, y: unifyTouchEvents(e).clientY };
    this.setState({ dragMode: true });
  };

  release = () => {
    this.setState({ dragMode: false });
  };

  drag = (e) => {
    if (this.state.noDrag) {
      return;
    }

    if (this.cursorPosition && this.state.dragMode) {
      const delta = { x: Math.round(unifyTouchEvents(e).clientX - this.cursorPosition.x),
        y: Math.round(unifyTouchEvents(e).clientY - this.cursorPosition.y) };
      this.setState(({ dragSizeHorizontal, dragSizeVertical, zoomRatio }) => ({
        dragSizeHorizontal: restrictMaxDragHorizontal(dragSizeHorizontal + delta.x, zoomRatio, this.imageRef.current),
        dragSizeVertical: restrictMaxDragVertical(dragSizeVertical + delta.y, zoomRatio, this.imageRef.current),
      }));
      this.cursorPosition = { x: unifyTouchEvents(e).clientX, y: unifyTouchEvents(e).clientY };
    }
  };

  render() {
    const { sku, images = [], hideModal } = this.props;
    const image = images[this.state.currentImageIndex];
    return (
      <Modal
        desktopLargeSize
        onCancel={hideModal}
        className="bop-galleryModal bop-galleryModal__noPinch"
        closeOnOuterClick={false}
      >
        <div
          className="bop-galleryModal__wrapper"
          ref={gallery => { this.wrapper = gallery; }}
        >
          <Button
            className="bop-gallery__zoomButton bop-gallery__closeModal"
            onClick={hideModal}
            secondary
          >
            <Icon name="close" className="gn-button__icon" />
          </Button>
          <div className="bop-galleryModal__images">
            <div className="bop-gallery__imageZoom">
              <img
                onLoad={this.adjustImageToWindow}
                className={classNames('bop-gallery__image--zoomable',
                  { 'bop-gallery__draggable': this.state.draggable },
                  { 'bop-gallery__dragged': this.state.draggable && this.state.dragMode })
                }
                style={{ '--bop-gallery__zoomRatio': this.state.zoomRatio,
                  '--bop-gallery__dragHorizontal': this.state.dragSizeHorizontal,
                  '--bop-gallery__dragVertical': this.state.dragSizeVertical,
                }}
                alt={`imageOfProduct id = ${sku}`}
                src={getProductImageUrl(sku, image, IMAGE_SIZES.S_1280)}
                ref={this.imageRef}
              />
            </div>
            <div
              className="bop-gallery__zoomButtonGroup"
              ref={buttonGroup => { this.buttonWrapper = buttonGroup; }}
            >
              {images.length > 1 && (
              <Button
                disabled={this.isFirstImageShown()}
                onClick={this.prevImage}
                className="bop-gallery__zoomButton bop-gallery__prev"
                secondary
              >
                <Icon name="chevron-left" className="gn-button__icon" />
              </Button>
              )}

              {images.length > 1 && (
              <Button
                disabled={this.isLastImageShown()}
                onClick={this.nextImage}
                className="bop-gallery__zoomButton bop-gallery__next"
                secondary
              >
                <Icon name="chevron-right" className="gn-button__icon" />
              </Button>
              )}
              <Button
                disabled={!shouldShowZoomOut(this.state.zoomRatio, this.state.initialRatio)}
                onClick={this.zoomOut}
                className="bop-gallery__zoomButton bop-gallery__zoomOut"
                secondary
              />
              <Button
                disabled={!shouldShowZoomIn(this.imageRef.current)}
                onClick={this.zoomIn}
                className="bop-gallery__zoomButton bop-gallery__zoomIn"
                secondary
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}
